import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import dayjs from 'dayjs';
import { makeStyles } from 'tss-react/mui';


import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import Fade from '@mui/material/Fade';

import * as XLSX from 'xlsx';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';

import MessagesReportModal from "../../components/MessagesReportModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

// hooks
import useSettings from '../../hooks/useSettings';
// components
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';

import RatingStars from "../../components/RatingStars";
import ToastError from "../../toast/error/toastError";


const reducer = (state, action) => {
  if (action.type === "LOAD_REPORT_TICKETS") {
    // Substitui completamente os tickets ao carregar nova página
    return action.payload;
  }

  if (action.type === "UPDATE_REPORT_TICKET") {
    const ticket = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticket.id);

    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
      return [...state];
    }
    return state;
  }

  if (action.type === "DELETE_REPORT_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);

    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles()((theme) => {
  return {
    mainPaper: {
      flex: 1,
      padding: theme.spacing(2),
      borderRadius: theme.spacing(2),
      boxShadow: theme.shadows[3]
    },
    filterCard: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3),
      borderRadius: theme.spacing(2),
      boxShadow: theme.shadows[2]
    },
    filterButton: {
      transition: "all 0.3s ease",
      borderRadius: theme.spacing(1),
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      }
    },
    iconTransition: {
      transition: "transform 0.3s ease"
    },
    tableCard: {
      borderRadius: theme.spacing(2),
      boxShadow: theme.shadows[3],
      overflow: 'hidden'
    },
    statusChip: {
      fontWeight: 600,
      borderRadius: theme.spacing(1)
    },
    emptyState: {
      padding: theme.spacing(8),
      textAlign: 'center'
    }
  }
});

const ReportTickets = () => {

  const { classes } = useStyles();

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [reportTickets, dispatch] = useReducer(reducer, []);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [quickCloseReportTicketsModal, setReportTicketsModalOpen] = useState(false);
  const { themeStretch } = useSettings();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [usersAttr, setUsersAttr] = useState([]);
  const { user } = useContext(AuthContext);
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState('all');
  const companyId = user.companyId;
  const [initialDate, setInitialDate] = useState(dayjs().subtract(7, 'days')); // Últimos 7 dias por padrão
  const [endDate, setEndDate] = useState(dayjs());
  const [status, setStatus] = useState('all');
  const [userAttr, setUserAttr] = useState('all');
  const [openMoreFilters, setOpenMoreFilters] = useState(false);
  const [filtroNotas, setFiltroNotas] = useState({ regra: "todos", valor: 0 });
  const notas = [0, 1, 2, 3, 4, 5];

  const handleChangeStatus = (event) => {
    setStatus(event.target.value);
    setPage(0); // Reset página ao mudar filtro
  };

  const handleChangeUserAttr = (event) => {
    setUserAttr(event.target.value);
    setPage(0);
  };

  const handleSelectedConnection = (event) => {
    setSelectedConnection(event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Carregar usuários
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", { getAllUsers: true });
        setUsersAttr(data.users);
      } catch (err) {
        ToastError(err);
      }
    };
    fetchUsers();
  }, []);

  // Carregar conexões
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const { data } = await api.get("/whatsapp/");
        setConnections(data);
      } catch (err) {
        ToastError(err);
      }
    };
    fetchConnections();
  }, []);

  // Carregar tickets com paginação SERVER-SIDE
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchReportTickets = async () => {
        try {
          const { data } = await api.get("/report-tickets", {
            params: {
              searchParam,
              pageNumber: page + 1, // Backend usa 1-indexed
              status,
              userId: userAttr,
              whatsappId: selectedConnection,
              initialDate: new Date(initialDate),
              endDate: new Date(endDate),
              limit: rowsPerPage // Envia limite de resultados
            }
          });

          // Filtrar números inválidos (grupos)
          const filteredData = data.tickets.map(ticket => {
            if (!ticket?.contact?.number) {
              return null;
            }

            if (String(ticket.contact.number).length > 13) {
              ticket.contact.number = 'Grupo';
            }

            return ticket;
          }).filter(Boolean);

          dispatch({ type: "LOAD_REPORT_TICKETS", payload: filteredData });
          setTotalCount(data.count);
          setHasMore(data.hasMore);
        } catch (err) {
          ToastError(err);
        } finally {
          setLoading(false);
        }
      };
      fetchReportTickets();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [page, rowsPerPage, searchParam, status, userAttr, selectedConnection, initialDate, endDate]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
    setPage(0);
  };

  const handleCloseReportMessageModal = () => {
    setSelectedTicketId(null);
    setReportTicketsModalOpen(false);
  };

  const handleModalMessage = (ticket) => {
    setSelectedTicketId(ticket);
    setReportTicketsModalOpen(true);
  }

  const renderStatus = (status) => {
    const statusConfig = {
      open: { color: '#3366ff', label: i18n.t("reportTickets.status.open") },
      closed: { color: '#616161', label: i18n.t("reportTickets.status.closed") },
      pending: { color: '#ffbf0aed', label: i18n.t("reportTickets.status.pending") }
    };

    const config = statusConfig[status];
    if (!config) return null;

    return (
      <Chip
        className={classes.statusChip}
        style={{ backgroundColor: config.color, color: '#fff' }}
        label={config.label}
        size="small"
      />
    );
  }

  const handleClickOpenMoreFilters = () => {
    setOpenMoreFilters(!openMoreFilters)
  }

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFiltroNotas(prevState => ({ ...prevState, [name]: value }));
  };

  // Filtro de notas (client-side pois é pós-processamento)
  const ticketsFiltereds = reportTickets.filter(ticket => {
    switch (filtroNotas.regra) {
      case 'maior':
        return +ticket?.rating >= filtroNotas.valor;
      case 'menor':
        return +ticket?.rating <= filtroNotas.valor;
      case 'igual':
        return +ticket?.rating === filtroNotas.valor;
      default:
        return true;
    }
  });

  const exportReport = () => {
    if (reportTickets.length === 0) {
      return ToastError(i18n.t("reportTickets.excel.error"));
    }

    const arquivoArray = ticketsFiltereds.map(ticket => ({
      [i18n.t("reportTickets.excel.name")]: ticket?.contact?.name,
      [i18n.t("reportTickets.excel.number")]: ticket?.contact?.number,
      [i18n.t("reportTickets.excel.createdAt")]: format(parseISO(ticket.createdAt), "dd/MM/yyyy HH:mm"),
      [i18n.t("reportTickets.excel.updatedAt")]: format(parseISO(ticket.updatedAt), "dd/MM/yyyy HH:mm"),
      [i18n.t("reportTickets.excel.status")]: i18n.t(`reportTickets.status.${ticket.status}`),
      [i18n.t("reportTickets.excel.protocol")]: ticket?.uuid,
      [i18n.t("reportTickets.excel.assignedUser")]: ticket?.user?.name,
      [i18n.t("reportTickets.excel.queue")]: ticket?.queue?.name,
      [i18n.t("reportTickets.excel.rating")]: ticket?.rating || i18n.t("reportTickets.excel.notRating"),
    }));

    const ws = XLSX.utils.json_to_sheet(arquivoArray);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Responses');
    XLSX.writeFile(wb, `${i18n.t("reportTickets.excel.archive")}_${format(new Date(), "dd-MM-yyyy")}.xlsx`);
  }

  return (
    <>
      <div style={{ marginTop: '65px' }}>
        <MessagesReportModal
          open={quickCloseReportTicketsModal}
          onClose={handleCloseReportMessageModal}
          ticket={selectedTicketId}
        />
        <Page title={i18n.t("reportTickets.title")}>
          <Container maxWidth={false}>
            <HeaderBreadcrumbs
              heading={i18n.t("reportTickets.title")}
              action={
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportReport}
                  disabled={loading || reportTickets.length === 0}
                >
                  {i18n.t("reportTickets.buttons.export")}
                </Button>
              }
            />

            {/* Card de Filtros */}
            <Card className={classes.filterCard}>
              <Grid container spacing={2} alignItems="center">
                {/* Linha 1: Busca e Datas */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={i18n.t("reportTickets.searchPlaceholder")}
                    type="search"
                    value={searchParam}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'pt-br'}>
                      <DesktopDatePicker
                        slotProps={{ textField: { size: 'small' } }}
                        label="Data inicial"
                        inputFormat="DD/MM/YYYY"
                        value={dayjs(initialDate)}
                        maxDate={dayjs(endDate)}
                        onChange={(newValue) => {
                          setInitialDate(newValue);
                          setPage(0);
                        }}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'pt-br'}>
                      <DesktopDatePicker
                        slotProps={{ textField: { size: 'small' } }}
                        label="Data final"
                        inputFormat="DD/MM/YYYY"
                        value={dayjs(endDate)}
                        minDate={dayjs(initialDate)}
                        maxDate={dayjs(new Date())}
                        onChange={(newValue) => {
                          setEndDate(newValue);
                          setPage(0);
                        }}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    className={classes.filterButton}
                    onClick={handleClickOpenMoreFilters}
                    startIcon={<FilterListIcon />}
                    endIcon={openMoreFilters ? <ExpandLess /> : <ExpandMore />}
                  >
                    Filtros
                  </Button>
                </Grid>
              </Grid>

              {/* Filtros Expandidos */}
              <Collapse in={openMoreFilters} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={status}
                          label="Status"
                          onChange={handleChangeStatus}
                        >
                          <MenuItem value="all">{i18n.t("reportTickets.status.all")}</MenuItem>
                          <MenuItem value="open">{i18n.t("reportTickets.status.open")}</MenuItem>
                          <MenuItem value="pending">{i18n.t("reportTickets.status.pending")}</MenuItem>
                          <MenuItem value="closed">{i18n.t("reportTickets.status.closed")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{i18n.t("reportTickets.table.connections")}</InputLabel>
                        <Select
                          value={selectedConnection}
                          label={i18n.t("reportTickets.table.connections")}
                          onChange={handleSelectedConnection}
                        >
                          <MenuItem value="all">{i18n.t("reportTickets.status.all")}</MenuItem>
                          {connections?.map((connection) => (
                            <MenuItem key={connection.id} value={connection.id}>
                              {connection.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{i18n.t("reportTickets.table.userAtt")}</InputLabel>
                        <Select
                          value={userAttr}
                          label={i18n.t("reportTickets.table.userAtt")}
                          onChange={handleChangeUserAttr}
                        >
                          <MenuItem value="all">{i18n.t("reportTickets.status.all")}</MenuItem>
                          {usersAttr?.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                              {user.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Stack direction="row" spacing={1}>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <InputLabel>Filtrar por Nota</InputLabel>
                          <Select
                            name="regra"
                            value={filtroNotas?.regra}
                            onChange={handleFilterChange}
                            label="Filtrar por Nota"
                          >
                            <MenuItem value="todos">
                              <em>Todos</em>
                            </MenuItem>
                            <MenuItem value="maior">Maior igual á</MenuItem>
                            <MenuItem value="menor">Menor igual á</MenuItem>
                            <MenuItem value="igual">Igual a</MenuItem>
                          </Select>
                        </FormControl>

                        {filtroNotas?.regra !== "todos" && (
                          <FormControl size="small" sx={{ minWidth: 80 }}>
                            <InputLabel>Nota</InputLabel>
                            <Select
                              label="Nota"
                              name="valor"
                              value={filtroNotas?.valor}
                              onChange={handleFilterChange}
                            >
                              {notas.map(nota => (
                                <MenuItem key={nota} value={nota}>{nota}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Card>

            {/* Card da Tabela */}
            <Card className={classes.tableCard}>
              {loading && <LinearProgress />}

              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell align="left">
                          {i18n.t("reportTickets.table.name")}
                        </TableCell>
                        <TableCell align="center">
                          {i18n.t("reportTickets.table.createdOn")}
                        </TableCell>
                        <TableCell align="center">
                          {i18n.t("reportTickets.table.updatedOn")}
                        </TableCell>
                        <TableCell align="center">
                          {i18n.t("reportTickets.table.status")}
                        </TableCell>
                        <TableCell align="center">
                          {i18n.t("reportTickets.table.userAtt")}
                        </TableCell>
                        <TableCell align="center">
                          {i18n.t("reportTickets.table.actions")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!loading && ticketsFiltereds.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Box className={classes.emptyState}>
                              <Typography variant="h6" color="text.secondary">
                                {i18n.t("reportTickets.table.noData") || "Nenhum ticket encontrado"}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Ajuste os filtros para ver os resultados
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        ticketsFiltereds.map((row) => {
                          const { id, contact, createdAt, updatedAt, user, status, rating } = row;
                          return (
                            <TableRow
                              hover
                              key={id}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell align="left">
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Avatar src={contact.profilePicUrl} alt={contact.name} />
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>{contact.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {contact.number}
                                    </Typography>
                                    {status === "closed" && rating && (
                                      <Box sx={{ mt: 0.5 }}>
                                        <RatingStars value={rating} isText={true} />
                                      </Box>
                                    )}
                                    {status === "closed" && !rating && (
                                      <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={i18n.t('userType.translate.closed')}
                                          color="primary"
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">
                                  {format(parseISO(createdAt), "dd/MM/yyyy HH:mm")}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">
                                  {format(parseISO(updatedAt), "dd/MM/yyyy HH:mm")}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">{renderStatus(status)}</TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">
                                  {user?.name || i18n.t("reportTickets.table.notUserAtt")}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip
                                  placement="left"
                                  TransitionComponent={Fade}
                                  TransitionProps={{ timeout: 600 }}
                                  title={i18n.t("reportTickets.buttons.viewTicketMsg")}
                                >
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleModalMessage(row)}
                                  >
                                    <SpeakerNotesIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                      {loading && <TableRowSkeleton columns={6} />}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
                labelRowsPerPage="Resultados por página:"
              />
            </Card>
          </Container>
        </Page>
      </div>
    </>
  );
};

export default ReportTickets;
